<template>
  <div>
    <p class="text-muted small">
      Cada capítulo tem o prompt padrão do sistema, mantido pela Arboran. Ao editar aqui,
      esta conta passa a usar a sua versão naquele capítulo — os demais continuam no padrão,
      e "Restaurar padrão" desfaz a personalização a qualquer momento.
    </p>

    <div class="row mb-3">
      <div class="col-lg-5 col-md-6">
        <b-form-input v-model="busca" placeholder="Buscar capítulo..." size="sm" />
      </div>
      <div class="col-lg-4 col-md-4 d-flex align-items-center">
        <b-form-checkbox v-model="somentePersonalizados" switch>
          <small>Somente personalizados</small>
        </b-form-checkbox>
      </div>
    </div>

    <div v-if="loading" class="loading-container">
      <RotateSquare class="loading-position animated fadeIn" size="60px" />
    </div>
    <b-table
      v-else
      :items="itensFiltrados"
      :fields="campos"
      :hover="true"
      responsive
      striped
      show-empty
      empty-text="Nenhum prompt encontrado."
    >
      <template v-slot:cell(stage_code)="data">
        {{ labelEtapa(data.item.stage_code) }}
      </template>
      <template v-slot:cell(prompt_type)="data">
        <b-badge :variant="data.item.prompt_type === 'system' ? 'dark' : 'primary'">
          {{ data.item.prompt_type === "system" ? "Instruções gerais" : "Prompt do capítulo" }}
        </b-badge>
      </template>
      <template v-slot:cell(is_custom)="data">
        <b-badge :variant="data.item.is_custom ? 'success' : 'secondary'">
          {{ data.item.is_custom ? "Personalizado" : "Padrão" }}
        </b-badge>
      </template>
      <template v-slot:cell(acoes)="data">
        <b-button variant="info" size="sm" title="Editar" @click="abrir(data.item)">
          <i class="fa fa-edit"></i>
        </b-button>
        <b-button
          v-if="data.item.is_custom"
          variant="secondary"
          size="sm"
          class="ml-1"
          title="Restaurar padrão"
          :disabled="restaurando"
          @click="restaurar(data.item)"
        >
          <i class="fa fa-undo"></i>
        </b-button>
      </template>
    </b-table>

    <!-- Modal editar prompt do capítulo (escopo da conta) -->
    <b-modal
      v-model="modalAberto"
      size="xl"
      scrollable
      :title="edicao ? edicao.name : 'Editar prompt'"
      ok-title="Salvar para esta conta"
      cancel-title="Cancelar"
      :ok-disabled="salvando || !conteudo.trim()"
      @ok="salvar"
      @hidden="fechar"
    >
      <template v-if="edicao">
        <p class="mb-2">
          <b-badge :variant="edicao.is_custom ? 'success' : 'secondary'" class="mr-2">
            {{ edicao.is_custom ? "Personalizado nesta conta" : "Usando o padrão" }}
          </b-badge>
          <small class="text-muted">{{ labelEtapa(edicao.stage_code) }}</small>
        </p>
        <p v-if="edicao.description" class="text-muted small">{{ edicao.description }}</p>

        <b-form-group
          :label="'Conteúdo do prompt (' + conteudo.length + ' caracteres)'"
          label-class="font-weight-bold"
        >
          <b-form-textarea v-model="conteudo" rows="18" class="pmau-prompt-area" />
        </b-form-group>

        <b-button variant="secondary" size="sm" class="mb-2" @click="verPadrao = !verPadrao">
          <i class="fa fa-eye mr-1"></i>{{ verPadrao ? "Ocultar" : "Ver" }} prompt padrão
        </b-button>
        <b-button
          v-if="conteudo !== edicao.default_content"
          variant="secondary"
          size="sm"
          class="mb-2 ml-2"
          @click="conteudo = edicao.default_content"
        >
          <i class="fa fa-undo mr-1"></i>Copiar o padrão para a edição
        </b-button>
        <b-form-textarea
          v-if="verPadrao"
          :value="edicao.default_content"
          rows="12"
          readonly
          class="pmau-prompt-area bg-light"
        />
      </template>
    </b-modal>
  </div>
</template>

<script>
import RotateSquare from "../../components/RotateSquare";
import PmauService from "../../servico/PmauService";
import { STAGE_LABELS, compararEtapa } from "../../enums/PmauStageLabels";

export default {
  name: "PmauContaPrompts",
  components: { RotateSquare },
  data() {
    return {
      loading: false,
      carregado: false,
      itens: [],
      busca: "",
      somentePersonalizados: false,
      modalAberto: false,
      edicao: null,
      conteudo: "",
      verPadrao: false,
      salvando: false,
      restaurando: false,
      campos: [
        { key: "stage_code", label: "Capítulo", sortable: true },
        { key: "prompt_type", label: "Tipo" },
        { key: "is_custom", label: "Situação", sortable: true },
        { key: "acoes", label: "Ações", thClass: "wd-80-px" },
      ],
    };
  },
  computed: {
    contaId() {
      const auth = this.$store.getters.getAutenticacao;
      return auth && auth.contaSelecionada ? auth.contaSelecionada.id : null;
    },
    itensFiltrados() {
      const busca = this.busca.trim().toLowerCase();
      return this.itens
        .filter((p) => !this.somentePersonalizados || p.is_custom)
        .filter((p) => {
          if (!busca) return true;
          return (
            this.labelEtapa(p.stage_code).toLowerCase().indexOf(busca) !== -1 ||
            (p.name || "").toLowerCase().indexOf(busca) !== -1
          );
        })
        .sort((a, b) => {
          const porEtapa = compararEtapa(a.stage_code, b.stage_code);
          return porEtapa !== 0 ? porEtapa : a.prompt_type.localeCompare(b.prompt_type);
        });
    },
  },
  methods: {
    labelEtapa(code) {
      return STAGE_LABELS[code] || code;
    },
    // Chamada ao abrir a aba: são ~150 prompts com o texto completo.
    carregar(forcar = false) {
      if (!this.contaId || (this.carregado && forcar !== true)) return;
      this.loading = true;
      PmauService.ListContaPrompts(this.contaId)
        .then((r) => {
          this.itens = r.data;
          this.carregado = true;
        })
        .catch(() => {
          this.$notify({ data: ["Erro ao carregar os prompts dos capítulos."], type: "warn", duration: 5000 });
        })
        .finally(() => { this.loading = false; });
    },
    abrir(item) {
      this.edicao = item;
      this.conteudo = item.content;
      this.verPadrao = false;
      this.modalAberto = true;
    },
    fechar() {
      this.edicao = null;
      this.conteudo = "";
      this.verPadrao = false;
    },
    salvar(evt) {
      if (evt) evt.preventDefault();
      const item = this.edicao;
      if (!item || !this.conteudo.trim()) return;
      this.salvando = true;
      PmauService.SaveContaPrompt(this.contaId, item.stage_code, item.prompt_type, this.conteudo)
        .then((r) => {
          this.aplicar(r.data);
          this.modalAberto = false;
          this.$notify({ data: ["Prompt salvo para esta conta."], type: "success", duration: 3000 });
        })
        .catch((e) => {
          const detalhe = e && e.response && e.response.data && e.response.data.detail;
          this.$notify({ data: [detalhe || "Erro ao salvar o prompt."], type: "error", duration: 5000 });
        })
        .finally(() => { this.salvando = false; });
    },
    restaurar(item) {
      this.restaurando = true;
      PmauService.ResetContaPrompt(this.contaId, item.stage_code, item.prompt_type)
        .then(() => {
          this.aplicar({ ...item, content: item.default_content, is_custom: false });
          this.$notify({ data: ["Prompt padrão restaurado."], type: "success", duration: 3000 });
        })
        .catch(() => {
          this.$notify({ data: ["Erro ao restaurar o prompt padrão."], type: "error", duration: 5000 });
        })
        .finally(() => { this.restaurando = false; });
    },
    aplicar(novo) {
      const i = this.itens.findIndex(
        (p) => p.stage_code === novo.stage_code && p.prompt_type === novo.prompt_type
      );
      if (i !== -1) this.$set(this.itens, i, { ...this.itens[i], ...novo });
    },
  },
};
</script>

<style scoped>
.pmau-prompt-area {
  font-family: "SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace;
  font-size: 0.82rem;
  line-height: 1.45;
  resize: vertical;
}
</style>
